/**
 * JOB-9 — putting a part on a job, from the phone that is standing next to it.
 *
 * Everything behind this has been built and tested for months: stock comes
 * off the right van, the serial feeds the install register and the docket,
 * the variation gate refuses a line when there is an unapproved change
 * outstanding, the offline queue carries it home. None of it had a screen.
 * A part could be taken off a job and not put on one.
 *
 * Three decisions shape what this looks like:
 *
 * **It reads the device, not the API.** Fitting a part happens in a comms
 * room, which is the worst reception in the building. The catalogue and the
 * van's levels are already on the phone, so the search is local and instant
 * and works with the aeroplane mode switch on.
 *
 * **The van count is on every row.** A tech choosing between two cameras
 * wants to know which one is in the back of the vehicle. Nothing is hidden
 * for being out of stock — a part that is physically in their hand gets
 * recorded whatever the count says, because the count is what is wrong in
 * that case, and a system that refuses the line teaches people to stop
 * recording parts.
 *
 * **The price is not shown and not asked for.** The price book decides it on
 * the server (D71). A tech fitting a camera is not negotiating, and a sell
 * price on a phone screen in front of a customer is a conversation nobody
 * wanted to have.
 */
import { useEffect, useRef, useState } from "react";

import * as actions from "../lib/actions";
import { ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { qty as fmtQty, money } from "../lib/format";
import { sellFromCost, sellFromMarkup } from "../lib/pricing";
import { MAX_MARKUP_PCT, pctToBp } from "../lib/pricemode";
import { useLocal } from "../lib/local";
import * as repo from "../lib/repo";
import { ScanButton, SerialScanner } from "./SerialScanner";
import { Card, Chip } from "./ui";

type Picked = {
  id: string;
  description: string;
  serialised: boolean;
  stocked: boolean;
  on_van: number;
  van_id: string | null;
};

/** "$42.50" or "42.5" to 4250. Null for anything that is not a number. */
function dollars(text: string): number | null {
  const value = Number(text.replace(/[$,\s]/g, ""));
  return Number.isFinite(value) && value >= 0 && text.trim() !== "" ? Math.round(value * 100) : null;
}

export function AddPart({
  jobId,
  onAdded,
  fixedPrice = false,
  onSaid,
}: {
  jobId: string;
  onAdded: () => void;
  /** The job bills its quote, so a priced part added here is cost tracking only (D80). */
  fixedPrice?: boolean;
  /**
   * Where to say what happened after an add. The job screen owns one message
   * beside the table, so an answer here replaces the last one rather than
   * standing next to it (D80).
   */
  onSaid?: (said: { tone: "info" | "crit" | "warn"; text: string } | null) => void;
}) {
  const { session } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Picked | null>(null);
  const [count, setCount] = useState("1");
  const [serial, setSerial] = useState("");
  const [warranty, setWarranty] = useState(false);
  const [typed, setTyped] = useState("");
  const [cost, setCost] = useState("");
  const [markupText, setMarkupText] = useState("");
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ tone: "info" | "crit" | "warn"; text: string } | null>(null);

  const [scanning, setScanning] = useState(false);
  //: Where the part is coming off. Empty string means "nowhere" — a real
  //: answer for a part out of the office's hand, and one somebody has to
  //: choose rather than fall into (D75).
  const [from, setFrom] = useState<string | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const saidRef = useRef<HTMLParagraphElement | null>(null);
  const list = useRef<HTMLDivElement | null>(null);

  // On a phone the panel opens at the bottom of a long card, and the results
  // land underneath the tab bar: a tech types "turret", sees an empty box,
  // and concludes the part is not in the price book. Nothing about that is
  // visible on a desktop, which is why it shipped (D73).
  // After an add the panel closes and the page gets shorter, which leaves the
  // answer — including "this won't be billed" — above the tech's screen (D80).
  useEffect(() => {
    if (said) saidRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [said]);

  // Default to the tech's own van. Anyone without one is asked, because a
  // line that quietly moves no stock is how a van's count stops matching it.
  useEffect(() => {
    if (picked === null) return;
    setFrom(picked.van_id ?? "");
  }, [picked]);

  useEffect(() => {
    if (!open) return;
    const target = !picked && search.trim() ? list.current : panel.current;
    // `end`, not `nearest`: the panel opens at the bottom of a long card on a
    // phone, and `nearest` scrolls the minimum — which left the quantity
    // field and the button under the tab bar. `scroll-margin-bottom` on the
    // elements keeps them clear of it (D75).
    target?.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [open, search, picked]);
  // The business's own target, so the preview matches what the server will
  // do with the same number.
  const { data: org } = useLocal(() => repo.org(), []);
  const margin = Number(org?.target_margin_bp ?? 0) || 0;
  const { data: places } = useLocal(
    () => (picked ? repo.placesFor(picked.id, session?.userId ?? "") : Promise.resolve([])),
    [picked?.id, session?.userId],
  );
  const { data: found } = useLocal(() => repo.fittable(search, session?.userId ?? ""), [search, session?.userId]);

  function reset() {
    setPicked(null);
    setSearch("");
    setCount("1");
    setSerial("");
    setWarranty(false);
    setFrom(null);
    setTyped("");
    setCost("");
    setMarkupText("");
  }

  async function add(body: Record<string, unknown>) {
    setBusy(true);
    setSaid(null);
    try {
      const { result, queued, message } = await actions.addLine(jobId, body);
      reset();
      setOpen(false);
      onAdded();
      // Two different things to say, and they are not interchangeable: "it is
      // on the job" and "the van you took it off is now showing less than
      // zero" (D20). The second is a bookkeeping problem, not a refusal.
      const warning = result?.stock?.warnings?.[0];
      // The server says when a part it has just recorded will not be billed;
      // the tech is the person who can raise a variation while still on site.
      const unbilled = result?.costing?.warnings?.find((w) => w.includes("fixed-price"));
      const answer: { tone: "info" | "crit" | "warn"; text: string } | null = unbilled
        ? { tone: "warn", text: unbilled }
        : warning
          ? { tone: "warn", text: warning }
          : queued
            ? { tone: "info", text: message ?? "Added on this phone." }
            : null;
      if (onSaid) onSaid(answer);
      else setSaid(answer);
    } catch (err) {
      setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Couldn't add that." });
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <>
        {said && (
          <p ref={saidRef} className={`notice notice-${said.tone}`} role={said.tone === "crit" ? "alert" : "status"}>
            {said.text}
          </p>
        )}
        <div className="btn-row" style={{ padding: "8px 14px" }}>
          <button className="btn btn-sm" type="button" onClick={() => setOpen(true)}>
            Add a part
          </button>
        </div>
      </>
    );
  }

  const number = Number(count);
  const sane = Number.isFinite(number) && number > 0;
  const paid = dollars(cost);
  // What the server will charge for it, worked out here only so the tech can
  // see it before they commit. The server decides (D71).
  // With a markup typed in, that is the price and it goes to the server as an
  // explicit one; without, the server prices at the business target margin (F3).
  const markup = pctToBp(markupText, MAX_MARKUP_PCT);
  const useMarkup = markup.valid && markup.bp !== null;
  const charge = paid === null ? 0 : useMarkup ? sellFromMarkup(paid, markup.bp ?? 0) : sellFromCost(paid, margin);

  return (
    <div ref={panel} className="add-part">
      <Card title="Add a part" padded>
        {said && (
          <p ref={saidRef} className={`notice notice-${said.tone}`} role={said.tone === "crit" ? "alert" : "status"}>
            {said.text}
          </p>
        )}

        {picked ? (
          <>
            <p style={{ margin: "0 0 10px" }}>
              <b>{picked.description}</b>{" "}
              {picked.stocked &&
                (picked.van_id === null ? (
                  // Not the same thing as an empty van, and saying "none on
                  // your van" to somebody who has no van is how an office
                  // user ends up believing stock moved (D75).
                  <Chip tone="neutral">no van of your own</Chip>
                ) : (
                  <Chip tone={picked.on_van > 0 ? "ok" : "warn"}>
                    {picked.on_van > 0 ? `${fmtQty(picked.on_van)} on your van` : "none on your van"}
                  </Chip>
                ))}
            </p>
            <div className="form-grid">
              <label className="field">
                <span>How many</span>
                <input inputMode="decimal" value={count} onChange={(e) => setCount(e.target.value)} autoFocus />
              </label>
              {picked.stocked && (
                <label className="field">
                  <span>Taken from</span>
                  <select value={from ?? ""} onChange={(e) => setFrom(e.target.value)}>
                    {(places ?? []).map((place) => (
                      <option key={place.id} value={place.id}>
                        {place.name} · {fmtQty(place.qty)}
                      </option>
                    ))}
                    {/* Last, and spelled out: recording no movement is a real
                        answer for a part bought on the way to site. */}
                    <option value="">Nowhere — don't change any count</option>
                  </select>
                </label>
              )}
              {picked.serialised && (
                <label className="field">
                  <span>
                    Serial <ScanButton onClick={() => setScanning(true)} />
                  </span>
                  <input
                    value={serial}
                    onChange={(e) => setSerial(e.target.value)}
                    placeholder="Scan it, or type it"
                    autoComplete="off"
                  />
                </label>
              )}
            </div>
            <label className="tiny" style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8 }}>
              <input type="checkbox" checked={warranty} onChange={(e) => setWarranty(e.target.checked)} />
              Under warranty — no charge
            </label>
            {fixedPrice && !warranty && (
              <p className="tiny muted" data-testid="fixed-price-note" style={{ margin: "8px 0" }}>
                This job is fixed price, so this won't go on the invoice. To charge for it, raise a variation; otherwise
                it comes out of the margin.
              </p>
            )}
            {picked.stocked && picked.van_id !== null && picked.on_van <= 0 && (
              <p className="tiny muted">
                Your van doesn't show any of these. Add it anyway if you fitted it — the count is what needs correcting,
                not the record.
              </p>
            )}
            {!sane && count.trim() !== "" && (
              <p className="notice notice-warn" role="status" style={{ marginTop: 8 }}>
                "{count}" isn't a quantity — it needs to be a number above zero.
              </p>
            )}
            <div className="btn-row" style={{ marginTop: 10 }}>
              <button
                className="btn btn-primary"
                type="button"
                disabled={busy || !sane}
                onClick={() =>
                  void add({
                    catalogue_item_id: picked.id,
                    kind: "part",
                    qty: number,
                    serial: serial.trim() || undefined,
                    under_warranty: warranty,
                    from_location_id: from || undefined,
                    // Explicit: an absent location means "my van", so saying
                    // nowhere needs its own word (D75).
                    moves_stock: from !== "",
                  })
                }
              >
                {busy ? "Adding…" : "Add to the job"}
              </button>
              <button className="btn" type="button" disabled={busy} onClick={() => setPicked(null)}>
                Back
              </button>
            </div>
          </>
        ) : (
          <>
            <label className="field">
              <span>From the price book</span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Camera, cable, reader…"
                autoFocus
              />
            </label>
            <div className="stack pick-list" ref={list}>
              {(found ?? []).map(({ item, stocked, on_van, van_id }) => (
                <button
                  key={String(item.id)}
                  type="button"
                  className="btn pick-row"
                  onClick={() =>
                    setPicked({
                      id: String(item.id),
                      description: String(item.description),
                      serialised: item.is_serialised === true,
                      stocked,
                      on_van,
                      van_id,
                    })
                  }
                >
                  <span>{String(item.description)}</span>
                  <span className="mono tiny muted">
                    {!stocked ? "" : on_van > 0 ? `${fmtQty(on_van)} on van` : "none on van"}
                  </span>
                </button>
              ))}
              {found && found.length === 0 && (
                <p className="tiny muted">
                  Nothing in the price book matches. Type it in below — the office can add it properly later.
                </p>
              )}
            </div>

            <p className="tiny muted" style={{ margin: "12px 0 4px" }}>
              Or something that isn't in the price book:
            </p>
            <div className="form-grid">
              <label className="field">
                <span>What it is</span>
                <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="2m flexible conduit" />
              </label>
              <label className="field">
                <span>How many</span>
                <input inputMode="decimal" value={count} onChange={(e) => setCount(e.target.value)} />
              </label>
              <label className="field">
                {/* Asked for, because the alternative is what this screen did
                  first: a real part saved at $0.00, invoiced at $0.00, with
                  nothing on the page to say so (D73). */}
                <span>What it cost</span>
                <input inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="$" />
              </label>
              <label className="field">
                <span>Markup % (optional)</span>
                <input
                  inputMode="decimal"
                  value={markupText}
                  onChange={(e) => setMarkupText(e.target.value)}
                  placeholder="usual margin"
                />
              </label>
            </div>
            <p className="tiny muted" style={{ marginTop: 6 }}>
              {paid === null
                ? "No cost yet, so this will go on the job at no charge."
                : useMarkup
                  ? `Charged at ${money(charge)} each — cost plus ${markupText.replace(/[%\s]/g, "")}%. The office can change it.`
                  : `Charged at ${money(charge)} each — your usual margin. The office can change it.`}
            </p>
            {!markup.valid && (
              <p className="notice notice-warn" role="status" style={{ marginTop: 8 }}>
                "{markupText}" isn't a markup — leave it blank, or use a percentage like 40.
              </p>
            )}
            {fixedPrice && (
              <p className="tiny muted" data-testid="fixed-price-note" style={{ margin: "8px 0" }}>
                This job is fixed price, so this won't go on the invoice. To charge for it, raise a variation; otherwise
                it comes out of the margin.
              </p>
            )}
            {!sane && count.trim() !== "" && (
              <p className="notice notice-warn" role="status" style={{ marginTop: 8 }}>
                "{count}" isn't a quantity — it needs to be a number above zero.
              </p>
            )}
            <div className="btn-row" style={{ marginTop: 10 }}>
              <button
                className="btn"
                type="button"
                disabled={busy || !typed.trim() || !sane || !markup.valid}
                onClick={() =>
                  void add({
                    kind: "part",
                    description: typed.trim(),
                    qty: number,
                    ...(paid === null ? {} : { unit_cost_cents: paid }),
                    // Only with a cost: a markup on nothing is not a price.
                    ...(paid !== null && useMarkup ? { unit_sell_cents: charge } : {}),
                  })
                }
              >
                Add it
              </button>
              <button
                className="btn"
                type="button"
                disabled={busy}
                onClick={() => {
                  reset();
                  setOpen(false);
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {scanning && (
          <SerialScanner
            what="serial"
            onSerial={(value) => {
              setSerial(value);
              setScanning(false);
            }}
            onClose={() => setScanning(false)}
          />
        )}
      </Card>
    </div>
  );
}
